/**
 * Ana durum makinesi — Bolum 4.5.
 *
 *                        +---------------- ertelendi (yapilamadi, yeniden planlanacak)
 *                        |
 *   giris --> kesif --> hazirlik --> kurulum --> kontrol --> ayakta
 *                                                   |          |
 *                                     "guvenli degil"          +--> revize --+ (onaylaninca ayakta'ya doner)
 *                                     -> kurulum'a doner       |
 *                                                              +--> sokum --> kapali
 *
 * Gecis izinleri kurallardan (rules.ts) beslenir; bu dosya kural TEKRARLAMAZ.
 */
import { OK, combine, fail, isYurutebilirMi, k1_revizeOnaylayabilirMi, k3_isEmriFotosuVarMi, sokumKapatilabilirMi, sokumGeriAlinabilirMi, teslimEdilebilirMi, } from './rules.js';
export const JOB_EVENTS = [
    'kesif_tamamlandi', // formen kesfi bitirdi: olculer + kurulum ekibi girildi
    'izin_kaydedildi', // musteri imzali izin (veya override)
    'malzeme_sevk', // "hazir ve sevk edildi"
    'kurulum_basladi', // "Başladım"
    'kurulum_bitti', // "Ben Kurdum" -> sef onayina duser
    'guvenli_degil', // guvenlik dongusu: kontrol -> kurulum
    'onayla_teslim', // TEK SERT GECIT (K1+K2+K3)
    'revize_acildi',
    'revize_onaylandi',
    'sokum_acildi',
    'sokum_kapandi',
    'sokum_geri_alindi', // sokum EMRI geri alindi -> iskele yerinde, is `ayakta` (Nuh 26.08.2026)
    'ertelendi',
    'iptal_edildi',
    'yeniden_planlandi',
];
/** Her gecis icin: hangi durumlardan cikilabilir ve hangi duruma varilir. */
const TRANSITIONS = {
    kesif_tamamlandi: { from: ['giris'], to: 'kesif' },
    // K4: izin ve malzeme ES ZAMANLIDIR — biri digerini kilitlemez.
    // Ikisi de tamamlandiginda `hazirlik`e gecilir; bu gecis `nextAfterHazirlik` ile hesaplanir.
    izin_kaydedildi: { from: ['kesif', 'hazirlik'], to: 'kesif' },
    malzeme_sevk: { from: ['kesif', 'hazirlik'], to: 'kesif' },
    /*
     * K5: kurulum baslangici IZNE BAGLI DEGIL (03.08.2026). Izin cevapsiz da
     * kalabilir; kapi teslimde (`k5_izinFotosuVarMi`). `kesif`ten dogrudan
     * baslanabilmesinin sebebi de bu.
     */
    kurulum_basladi: { from: ['kesif', 'hazirlik'], to: 'kurulum' },
    kurulum_bitti: { from: ['kurulum'], to: 'kontrol' },
    guvenli_degil: { from: ['kontrol'], to: 'kurulum' },
    onayla_teslim: { from: ['kontrol'], to: 'ayakta' },
    revize_acildi: { from: ['ayakta'], to: 'revize' },
    revize_onaylandi: { from: ['revize'], to: 'ayakta' },
    sokum_acildi: { from: ['ayakta'], to: 'sokum' },
    sokum_kapandi: { from: ['sokum'], to: 'kapali' },
    /**
     * SOKUM EMRINI GERI AL — iskele yerinde kalir, is `ayakta`ya doner.
     *
     * ⚠️ BU BIR "IS IPTALI" DEGIL. Nuh 26.08.2026'da *"yanlislikla baslatilan
     *    veya karistirilan bir sokum isinin iptal edilmesi mumkun olmuyor"*
     *    dedi. Iptal edilen sey SOKUM EMRIDIR; iskele hala ayaktadir ve is
     *    kurulu hayatina devam eder. `iptal_edildi` bambaska bir sey soyler
     *    ("bu is hic yapilmayacak") ve zaten `ayakta`dan yapilamaz.
     */
    sokum_geri_alindi: { from: ['sokum'], to: 'ayakta' },
    ertelendi: { from: ['giris', 'kesif', 'hazirlik', 'kurulum'], to: 'ertelendi' },
    /**
     * ⚠️ IPTAL, `ayakta`dan YAPILAMAZ. Kurulu bir iskele "iptal" edilemez —
     *    sokulmesi gerekir. Iptal, HENUZ KURULMAMIS bir isi kapatmaktir.
     */
    iptal_edildi: { from: ['giris', 'kesif', 'hazirlik', 'kurulum', 'ertelendi'], to: 'iptal' },
    /** Yanlislikla ertelenen ya da iptal edilen is akisa geri alinabilir (denetlenir). */
    yeniden_planlandi: { from: ['ertelendi', 'iptal'], to: 'giris' },
};
/**
 * Bir gecis, YALNIZCA DURUM acisindan mumkun mu? Yetki ve is kurallari haric.
 *
 * ⚠️ Karar mercii bu DEGIL, `canTransition`. Bu yardimci yalnizca UI'nin
 *    "bu baglanti gorunsun mu, gorunmuyorsa NEDEN" sorusu icindir. Ekranlar
 *    durum listesini ELLE yazmasin diye var: iptal linki `['giris','kesif',
 *    'hazirlik','kurulum']` diye kopyalanmisti ve `ertelendi` atlanmisti —
 *    durum makinesi izin verdigi hâlde ekranda yol yoktu.
 */
export function durumIzinVeriyorMu(durum, event) {
    return TRANSITIONS[event].from.includes(durum);
}
/**
 * Malzeme sevk edildiyse `hazirlik`e terfi et.
 * Bu bir olay degil, turetilmis bir durumdur (K4).
 *
 * ⚠️ IZIN ARTIK SART DEGIL (03.08.2026). Once `izin != null && malzemeSevk`
 *    araniyordu; izin cevapsizken is `kesif`te takiliyor ve arayuzde kurulum
 *    adimi ASMA KILITLI kaliyordu. Musteri karari: izin cevapsiz kalabilir,
 *    tek kapi teslimde. Malzeme sarti duruyor — malzeme sahada yoksa kurulum
 *    zaten fiilen baslayamaz.
 */
export function hazirlikDurumu(job) {
    if (job.durum !== 'kesif')
        return job.durum;
    return job.malzemeSevk ? 'hazirlik' : 'kesif';
}
/** Gecis yapilabilir mi — hem durum makinesi hem is kurallari kontrol edilir. */
export function canTransition(job, event, ctx) {
    const t = TRANSITIONS[event];
    if (!t.from.includes(job.durum)) {
        return fail({
            code: 'STATE',
            message: `"${job.durum}" durumundaki bir iş için bu işlem yapılamaz.`,
        });
    }
    switch (event) {
        case 'onayla_teslim':
            return teslimEdilebilirMi(job, ctx.actor);
        case 'revize_onaylandi':
            return k1_revizeOnaylayabilirMi(job, ctx.actor);
        case 'sokum_kapandi':
            return sokumKapatilabilirMi(job);
        case 'sokum_geri_alindi':
            return sokumGeriAlinabilirMi(job, ctx.actor);
        /*
         * OPERASYONEL ADIMLAR — işe atanmamış kullanıcı yürütemez (yalnız şef).
         * Keşif, kurulum başlat/bitti ve "güvenli değil → geri gönder". Denetimde
         * bulunan IDOR sınıfını (herkes her işi yürütebiliyordu) kapatır.
         */
        case 'kesif_tamamlandi':
        case 'kurulum_basladi':
        case 'kurulum_bitti':
        case 'guvenli_degil':
            return isYurutebilirMi(job, ctx.actor);
        // Revize ve sokum YALNIZ `ayakta` iken acilabilir. Devam eden bir faz varken
        // ikinci bir faz acilamaz — kullanicinin acik talebi (Bolum 6.3).
        case 'revize_acildi':
        case 'sokum_acildi':
            return combine(job.aktifRevize != null
                ? fail({ code: 'STATE', message: 'Devam eden bir revize varken yeni faz açılamaz.' })
                : OK, job.sokum != null
                ? fail({ code: 'STATE', message: 'Devam eden bir söküm varken yeni faz açılamaz.' })
                : OK);
        case 'ertelendi':
        case 'iptal_edildi':
            // "Yapilamadi" nedeni is emri arka yuzunden gelir; API katmani zorunlu kilar.
            return OK;
        default:
            return OK;
    }
}
/** Gecisin varacagi durum. `izin_kaydedildi` / `malzeme_sevk` icin turetilmis terfi uygulanir. */
export function nextStatus(job, event) {
    if (event === 'izin_kaydedildi' || event === 'malzeme_sevk') {
        // Cagiran taraf ilgili alani guncelledikten SONRA bu fonksiyonu cagirir.
        return hazirlikDurumu(job);
    }
    return TRANSITIONS[event].to;
}
/**
 * K3 — kapanis gecidi. `onayla_teslim` ve `sokum_kapandi` her ikisi de is emri
 * kagit fotosunu zorunlu kilar; API katmaninda tek noktadan kontrol icin.
 */
export function kapanisBloklandiMi(job) {
    return k3_isEmriFotosuVarMi(job);
}
/** Bolum 6.3 — Revizyon/Söküm/Kontrol aksiyon butonlari YALNIZ `ayakta` iken gorunur. */
export function fazAcilabilirMi(job) {
    return job.durum === 'ayakta' && job.aktifRevize == null && job.sokum == null;
}
//# sourceMappingURL=state-machine.js.map